import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

import { metricsAxios, proxyAxios } from 'api/apiBase/axios';

import type { RestRequestOptions } from './types';

const PROXY_PATH = '/api/v1/proxy';

const buildUrlWithParams = (url: string, params?: AxiosRequestConfig['params']): string => {
  if (!params || (Array.isArray(params) && params.length === 0)) {
    return url;
  }

  const searchParams = new URLSearchParams();

  const appendParam = (key: string, value: unknown) => {
    if (value === undefined || value === null) {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach(item => appendParam(key, item));
      return;
    }

    searchParams.append(key, String(value));
  };

  if (typeof params === 'object' && !Array.isArray(params)) {
    Object.entries(params).forEach(([key, value]) => appendParam(key, value));
  }

  const query = searchParams.toString();
  if (!query) {
    return url;
  }

  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${query}`;
};

// Both the `proxyAxios` and `metricsAxios` transports resolve to the same API
// host, path and Bearer auth, so they share one POST contract that matches the
// real API DTO `request.ProxyPostRequest{ url, body string }`. The API forwards
// `in.Body` verbatim as the upstream request body, so the serialized payload
// must always be sent as the `body` string field.
const createExchangeProxyClient = (
  transport: AxiosInstance,
): Pick<AxiosInstance, 'get' | 'post'> => ({
  get<T = unknown, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig) {
    const { params, signal, timeout, headers } = config ?? {};
    const targetUrl = buildUrlWithParams(url, params);

    const requestConfig: AxiosRequestConfig = {
      signal,
      timeout,
      headers,
      params: {
        url: targetUrl,
      },
    };

    return transport.get<T, R>(PROXY_PATH, requestConfig);
  },
  post<T = unknown, R = AxiosResponse<T>>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ) {
    const { params, signal, timeout, headers } = config ?? {};
    const targetUrl = buildUrlWithParams(url, params);
    const body = data === undefined ? '' : typeof data === 'string' ? data : JSON.stringify(data);

    if (body === undefined) {
      throw new TypeError('Proxy POST body must be JSON-serializable');
    }

    const requestConfig: AxiosRequestConfig = {
      signal,
      timeout,
      headers,
    };

    return transport.post<T, R>(PROXY_PATH, { url: targetUrl, body }, requestConfig);
  },
});

const exchangeProxyClient = createExchangeProxyClient(proxyAxios);
const metricsExchangeProxyClient = createExchangeProxyClient(metricsAxios);

export const proxyRestOptions: RestRequestOptions = {
  client: exchangeProxyClient as AxiosInstance,
};

export const metricsProxyRestOptions: RestRequestOptions = {
  client: metricsExchangeProxyClient as AxiosInstance,
};
